import React, { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import If from 'src/components/Shared/If/If';
import Phone from 'src/components/Shared/Phone/Phone';
import config from 'src/config/config';
import { setProgress } from 'src/redux/modules/progress';
import { getUncollectedSharesLookup } from 'src/redux/modules/sellUncollectedShares/selectors';
import {
  EARLY_WITHDRAWAL_PENALTY_AGE,
  FundNoteSell,
  APP_SUPPORT_PHONE_NUMBER,
  RAS_SUPPORT_PHONE_NUMBER,
  TOLL_FREE_NUMBER,
} from 'src/settings/settings';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import ACHRedemptionFundNoteTip from './ACHRedemptionFundNoteTip';
import AddressChangePendingFundNoteTip from './AddressChangePendingFundNoteTip';
import AddressUndeliverableFundNoteTip from './AddressUndeliverableFundNoteTip';
import DeliveryMethodFundNoteTip from './DeliveryMethodFundNoteTip';
import ESAPlanFundNoteTip from './ESAPlanFundNoteTip';
import FundNote from './FundNote';
import './FundNote.module.scss';
import PendingTransactionFundNoteTip from './PendingTransactionFundNoteTip';
import SignatureRequiredFundNoteTip from './SignatureRequiredFundNoteTip';
import UncollectedFundNoteTip from './UncollectedFundNoteTip';
import WireRedemptionFundNoteTip from './WireRedemptionFundNoteTip';
import { isAPP, isRAS } from '../../../../utils/advice/advice-utility';
import { getSelectedDeliveryMethod } from 'src/redux/modules/sellDeliveryMethod/sellDeliveryMethod.selectors';
import { ThunkDispatch } from 'redux-thunk';
import styles from '../../../Shared/APPText/APPText.module.scss';

const SellFundNotes = ({ fund }) => {
  const phoneNumber = isAPP(fund.productType) ? (
    <Phone phoneNumber={APP_SUPPORT_PHONE_NUMBER} />
  ) : isRAS(fund.productType) ? (
    <Phone phoneNumber={RAS_SUPPORT_PHONE_NUMBER} />
  ) : (
    <Phone phoneNumber={TOLL_FREE_NUMBER} />
  );
  const selectedDeliveryMethod = useSelector(getSelectedDeliveryMethod);
  // @ts-ignore
  const { sellUncollectedShares } = useSelector((state) => state);
  const uncollectedSharesLookup = useSelector(getUncollectedSharesLookup);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  return (
    <Fragment>
      <If
        test={fund.notes[FundNoteSell.BANK_MISSING_WIRE_MAIL_SERVICE_FEATURES]}
      >
        <FundNote
          labelSubText=""
          labelText="Wire & Check Redemptions Unavailable"
          tipHeader="Wire & Check Redemptions Unavailable"
          tipMessage={
            <div className="exclude">
              Your bank account is not set up to receive redemptions via wire or
              mailed check. To complete this transaction, try selecting a
              different bank account or choose to redeem via direct deposit
              (ACH).
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.PENDING_STATUS]}>
        <FundNote labelText="Pending Bank Association" showToolTip={false} />
      </If>
      <If test={fund.notes[FundNoteSell.BANK_MISSING_WIRE_SERVICE_FEATURES]}>
        <FundNote
          labelSubText=""
          labelText="Wire Redemptions Unavailable"
          tipHeader="Wire Redemptions Unavailable"
          tipMessage={
            <div className="exclude">
              Your bank account is not set up to receive redemptions via wire.
              To complete this transaction, try selecting a different bank
              account or choose to redeem via a mailed check or direct deposit
              (ACH).
            </div>
          }
        />
      </If>
      <If
        test={fund.notes[FundNoteSell.BANK_MISSING_ACH_MAIL_SERVICE_FEATURES]}
      >
        <FundNote
          labelSubText=""
          labelText="Direct Deposit & Check Unavailable"
          tipHeader="Direct Deposit & Check Unavailable"
          tipMessage={
            <div className="exclude">
              Your bank account is not set up to receive redemptions via direct
              deposit (ACH) or mailed check. To complete this transaction, try
              selecting a different bank account or choose to redeem via wire
              transfer.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.BANK_MISSING_ACH_SERVICE_FEATURES]}>
        <FundNote
          labelSubText=""
          labelText="Direct Deposit Unavailable"
          tipHeader="Direct Deposit Unavailable"
          tipMessage={
            <div className="exclude">
              Your bank account is not set up to receive redemptions via direct
              deposit (ACH). To complete this transaction, try selecting a
              different bank account or choose to redeem via a mailed check or
              wire transfer.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.BANK_MISSING_ALL_SERVICE_FEATURES]}>
        <FundNote
          labelSubText=""
          labelText="Redemption Methods Unavailable"
          tipHeader="Redemption Methods Unavailable"
          tipMessage={
            <div className="exclude">
              Your bank account is not set up to receive redemptions via wire or
              direct deposit (ACH), or mailed check. Please call an associate at{' '}
              <Phone phoneNumber={TOLL_FREE_NUMBER} /> to enable these
              redemptions online.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.BANK_MISSING_SERVICE_FEATURES]}>
        <FundNote
          labelSubText=""
          labelText="Wire & Direct Deposit Unavailable"
          tipHeader="Wire & Direct Deposit Unavailable"
          tipMessage={
            <div className="exclude">
              Your bank account is not set up to receive redemptions via wire or
              direct deposit (ACH). To complete this transaction, try selecting
              a different bank account or choose to redeem via a mailed check.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.MONEY_MARKET_EVENT_GATE_ACTIVE]}>
        <FundNote
          labelSubText={
            <div
              dangerouslySetInnerHTML={{
                __html: fund.moneyMarketReformMessage,
              }}
              className="exclude"
            />
          }
          labelText="Redemption Block"
          showToolTip={false}
        />
      </If>
      <If test={fund.notes[FundNoteSell.MONEY_MARKET_EVENT_GATE_REMOVED]}>
        <FundNote
          labelSubText={
            <div
              dangerouslySetInnerHTML={{
                __html: fund.moneyMarketReformMessage,
              }}
              className="exclude"
            />
          }
          labelText="Redemption Block Removed"
          showToolTip={false}
        />
      </If>
      <If test={fund.notes[FundNoteSell.MONEY_MARKET_EVENT_FEE_ACTIVE]}>
        <FundNote
          labelSubText={
            <div
              dangerouslySetInnerHTML={{
                __html: fund.moneyMarketReformMessage,
              }}
              className="exclude"
            />
          }
          labelText="Liquidity Fee"
          showToolTip={false}
        />
      </If>
      <If test={fund.notes[FundNoteSell.MONEY_MARKET_EVENT_FEE_REMOVED]}>
        <FundNote
          labelSubText={
            <div
              dangerouslySetInnerHTML={{
                __html: fund.moneyMarketReformMessage,
              }}
              className="exclude"
            />
          }
          labelText="Liquidity Fee Removed"
          showToolTip={false}
        />
      </If>
      <If test={fund.notes[FundNoteSell.NO_RAS_IN_KIND_TRANSFER]}>
        <FundNote
          labelSubText=""
          labelText="In-Kind Transfer Unavailable"
          showToolTip={false}
        />
      </If>
      <If test={fund.notes[FundNoteSell.IRA_DECEASED]}>
        <FundNote
          labelSubText=""
          labelText="Change of Ownership Needed"
          tipHeader="Change of Ownership Needed"
          tipMessage={
            <div className="exclude">
              Change of Ownership Needed: Please{' '}
              <a
                className="exclude"
                href={`${config.url}/personal-investing/help/contact/index.html`}
                rel="noopener noreferrer"
                target="_blank"
              >
                contact us
              </a>{' '}
              to discuss options available with this account.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.INHERITED_FUND]}>
        <FundNote
          labelSubText=""
          labelText="Inherited IRA"
          tipHeader="Inherited IRA"
          tipMessage={
            <div className="exclude">
              Inherited IRA accounts are not eligible for online transactions.
              Please{' '}
              <a
                className="exclude"
                href="https://www.troweprice.com/personal-investing/help/contact/index.html"
                rel="noreferrer"
                target="_blank"
              >
                contact us
              </a>{' '}
              to redeem from your Inherited IRA.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.SEP_IRA]}>
        <FundNote
          labelSubText=""
          labelText="SEP IRA"
          tipHeader="SEP IRA"
          tipMessage={
            <div className="exclude">
              SEP IRA accounts are not eligible for online transactions. To
              redeem from your SEP IRA account,{' '}
              <a
                className="exclude"
                href="https://www.troweprice.com/personal-investing/help/contact/index.html"
                rel="noreferrer"
                target="_blank"
              >
                contact us
              </a>
              .
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.NON_FIDUCIARY_FUND]}>
        <FundNote
          labelSubText=""
          labelText="Non-Fiduciary Account"
          tipHeader="Non-Fiduciary Account"
          tipMessage={
            <div className="exclude">
              Non-Fiduciary accounts are not eligible for online redemptions. To
              redeem from your Non-Fiduciary account, please{' '}
              <a
                className="exclude"
                href="https://www.troweprice.com/personal-investing/help/contact/index.html"
                rel="noreferrer"
                target="_blank"
              >
                contact us
              </a>
              .
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.STOP_TRANSFER]}>
        <FundNote
          labelSubText=""
          labelText="Stop Transfer"
          tipHeader="Stop Transfer"
          tipMessage={
            <div className="exclude">
              A stop transfer has been applied to this fund account. Please{' '}
              <a
                className="exclude"
                href="https://www.troweprice.com/personal-investing/help/contact/index.html"
                rel="noreferrer"
                target="_blank"
              >
                contact us
              </a>{' '}
              to redeem from this fund account.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.AUTOMATED_SYSTEMS_STOP_REDEMPTION]}>
        <FundNote
          labelSubText=""
          labelText="Unavailable Online"
          tipHeader="Unavailable Online"
          tipMessage={
            <div className="exclude">
              Online/phone redemption disabled. Please
              <a
                className="exclude"
                href="https://www.troweprice.com/personal-investing/help/contact/index.html"
                rel="noreferrer"
                target="_blank"
              >
                contact us
              </a>{' '}
              to enable redemptions.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.UNAVAILABLE_FOR_REDEMPTION_ONLINE]}>
        <FundNote
          labelSubText=""
          labelText="Unavailable Online"
          tipHeader="Unavailable Online"
          tipMessage={
            <div className="exclude">
              Online/phone redemption disabled.{' '}
              <a
                className="exclude"
                href="https://www.troweprice.com/personal-investing/help/contact/index.html"
                rel="noreferrer"
                target="_blank"
              >
                Contact us
              </a>{' '}
              to enable redemptions.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.PENALTY_WITHHOLDING]}>
        <FundNote
          labelSubText=""
          labelText="IRS Backup or Penalty Tax Withholding"
          tipHeader="IRS Backup or Penalty Tax Withholding"
          tipMessage={
            <div className="exclude">
              There is an IRS backup or penalty tax withholding restriction on
              this fund account. To learn more about these restrictions or make
              a transaction, please{' '}
              <a
                className="exclude"
                href={`${config.url}/personal-investing/help/contact/index.html`}
                rel="noopener noreferrer"
                target="_blank"
              >
                contact us
              </a>
              .
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.UNAVAILABLE_FOR_ACH_REDEMPTION_ONLINE]}>
        <FundNote
          labelSubText=""
          labelText="Direct Deposit Unavailable"
          tipHeader="Direct Deposit Unavailable"
          tipMessage={<ACHRedemptionFundNoteTip fund={fund} />}
        />
      </If>
      <If
        test={fund.notes[FundNoteSell.UNAVAILABLE_FOR_WIRE_REDEMPTION_ONLINE]}
      >
        <FundNote
          labelSubText=""
          labelText="Wire Redemption Unavailable"
          tipHeader="Wire Redemption Unavailable"
          tipMessage={<WireRedemptionFundNoteTip fund={fund} />}
        />
      </If>
      <If test={fund.notes[FundNoteSell.ESA_PLAN]}>
        <FundNote
          labelSubText=""
          labelText="ESA Plan"
          tipHeader="ESA Plan"
          tipMessage={<ESAPlanFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteSell.ADDRESS_UNDELIVERABLE]}>
        <FundNote
          labelSubText=""
          labelText="Address Undeliverable"
          tipHeader="Address Undeliverable"
          tipMessage={<AddressUndeliverableFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteSell.CANNOT_LINK_BANK]}>
        <FundNote
          labelSubText="Signature Required"
          labelText="Cannot Link Bank"
          tipHeader="Cannot Link Bank"
          tipMessage={<SignatureRequiredFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteSell.BANK_NOT_LINKED]}>
        <FundNote
          labelSubText=""
          labelText="Bank Not Linked"
          tipHeader="Bank Not Linked"
          tipMessage={
            <div className="exclude bankNotLinked">
              This fund is not connected to a bank on file. To continue,{' '}
              <a
                className="exclude text-nowrap college-branded-link"
                href={`${config.url}/usis/my-accounts/profile/linked-bank-accounts`}
              >
                link this bank
              </a>{' '}
              or{' '}
              <a
                className="exclude text-nowrap college-branded-link"
                onClick={() => {
                  dispatch(setProgress(1));
                  scrollElementIntoFullView('funding-target-step');
                }}
              >
                choose a different delivery method.
              </a>
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.ADDRESS_CHANGE_PENDING]}>
        <FundNote
          labelSubText=""
          labelText="Address Change"
          tipHeader="Address Change Pending"
          tipMessage={<AddressChangePendingFundNoteTip fund={fund} />}
        />
      </If>
      <If test={fund.notes[FundNoteSell.UNCOLLECTED_SHARES]}>
        <FundNote
          labelSubText=""
          labelText="Uncollected Shares"
          tipHeader={'Uncollected Shares'}
          tipMessage={
            <UncollectedFundNoteTip
              fund={fund}
              sellUncollectedShares={sellUncollectedShares}
              uncollectedSharesLookup={uncollectedSharesLookup}
            />
          }
        />
      </If>
      <If
        test={
          fund.notes[FundNoteSell.UNAVAILABLE_DELIVERY] &&
          selectedDeliveryMethod === 'Check'
        }
      >
        <FundNote
          labelSubText=""
          labelText="Delivery Method Unavailable"
          tipHeader={'Check Redemption Unavailable'}
          tipMessage={<DeliveryMethodFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteSell.PENDING]}>
        <FundNote
          labelSubText=""
          labelText="Transaction Pending"
          tipHeader="Transaction Pending"
          tipMessage={<PendingTransactionFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteSell.PENDING_TRANSACTION]}>
        <FundNote
          labelSubText=""
          labelText="Transaction Pending"
          tipHeader="Transaction Pending"
          tipMessage={<PendingTransactionFundNoteTip allFunds />}
        />
      </If>
      <If test={fund.notes[FundNoteSell.RAS_EARLY_IRA_WITHDRAWAL]}>
        <FundNote
          labelSubText=""
          labelText="Early IRA Withdrawal"
          tipHeader="Early IRA Withdraw"
          tipMessage={
            <div className="exclude">
              A 10% additional tax for early withdrawal may apply for
              distributions made before reaching age{' '}
              {EARLY_WITHDRAWAL_PENALTY_AGE}. Please talk to your advisor before
              proceeding with this transaction.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteSell.INELIGIBLE_BALANCE]}>
        <FundNote
          labelSubText=""
          labelText="Ineligible Balance"
          tipHeader="Ineligible Balance"
          tipMessage={
            <div className="exclude">
              ActivePlus Portfolios<sup className={styles.sup}>®</sup> accounts
              require a minimum balance of $10,000 to stay in the service. If
              you would like to proceed with closing out your account, please
              select "Sell all investments & close account"
              <br />
              <br />
              For more information, please call {phoneNumber}.
            </div>
          }
        />
      </If>
    </Fragment>
  );
};

export default SellFundNotes;
